import { useState, useEffect, useLayoutEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { Accordion, Card, Col, Container, Row, Table } from 'react-bootstrap';
import Badge from '../../../components/base/Badge';
import { selectProjectResults } from '../project-details/data-access/project.reducer';
import { FairnessPlotsSection, type FairnessResultRow } from './FairnessPlotsSection';
import { FairnessModuleKpiGrid } from './ModuleKpiDashboard';
import './FairnessPlotsSection.css';
import './BiasDetectionChartsSection.css';
import './BiasAggregateSummaryPanel.css';
import VersionHistory, { VersionItem } from '../../../components/common/VersionHistory';
import { isWorkbenchNonRerunRun } from '../../../utils/workbenchRunFilter';
import { pickLatestCollectionRun } from '../../../utils/collectionRuns';
import { Typography } from '@mui/material';

function readPhoenixNavbarTopPx(): number {
  if (typeof window === 'undefined') return 72;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--phoenix-navbar-top-height').trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 72;
}

function toFairnessResultRow(entry: Record<string, unknown>): FairnessResultRow {
  const asNumber = (value: unknown): number | null => {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  return {
    attribute: String(entry.attribute ?? entry.Attribute ?? ''),
    coverage: String(entry.coverage ?? entry.Coverage ?? ''),
    status: String(entry.status ?? entry.Status ?? ''),
    audit_status: String(entry.audit_status ?? entry.AuditStatus ?? entry.auditStatus ?? ''),
    avg_A: asNumber(entry.avg_A ?? entry.avgA ?? entry.avg_a),
    avg_B: asNumber(entry.avg_B ?? entry.avgB ?? entry.avg_b),
    disparity: asNumber(entry.disparity ?? entry.Disparity),
    confidence: Number(asNumber(entry.confidence ?? entry.Confidence) ?? 0),
    sensitivity: Number(asNumber(entry.sensitivity ?? entry.Sensitivity) ?? 0)
  };
}

/** Enterprise ML matrix rows (`ml_entries`): disparate_impact, equalized_odds, risk — map into Python-style rows for plots. */
function mlEnterpriseEntryToFairnessRow(entry: Record<string, unknown>): FairnessResultRow {
  const asNumber = (value: unknown): number | null => {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const di = asNumber(entry.disparate_impact) ?? asNumber(entry['Disparate Impact']);
  let disparity: number | null = null;
  if (di != null && Number.isFinite(di)) {
    disparity = Math.min(1, Math.max(0, Math.abs(di - 1)));
  }
  const riskRaw = String(entry.risk ?? entry.Risk ?? '')
    .trim()
    .toLowerCase();
  let status = 'MONITOR';
  if (riskRaw === 'high') status = 'UNFAIR';
  else if (riskRaw === 'low') status = 'FAIR';

  return {
    attribute: String(entry.attribute ?? entry.Attribute ?? ''),
    coverage: String(entry.coverage ?? entry.Coverage ?? 'ELIGIBLE'),
    status,
    audit_status: String(entry.audit_status ?? entry.AuditStatus ?? 'FAIR'),
    avg_A: null,
    avg_B: null,
    disparity,
    confidence: Number(asNumber(entry.confidence ?? entry.Confidence) ?? 0),
    sensitivity: Number(asNumber(entry.sensitivity ?? entry.Sensitivity) ?? 0)
  };
}

function entryToMlFairnessRow(entry: Record<string, unknown>): FairnessResultRow {
  const hasEnterpriseMl =
    entry.disparate_impact != null ||
    entry['Disparate Impact'] != null ||
    entry.risk != null ||
    entry.Risk != null ||
    entry.equalized_odds != null ||
    entry['Equalized Odds'] != null;
  if (hasEnterpriseMl) return mlEnterpriseEntryToFairnessRow(entry);
  return toFairnessResultRow(entry);
}

function analysisType(raw: unknown): string {
  return String(raw ?? '')
    .trim()
    .toLowerCase();
}

/** Supports `analysis_type`, `analysisType`, and legacy kind fields from different API layers. */
function inferAnalysisType(source: Record<string, unknown> | null | undefined): string {
  if (!source) return '';
  const hasMlMetric =
    (Array.isArray(source.ML_Fairness_Metric) && source.ML_Fairness_Metric.length > 0) ||
    (Array.isArray(source.ml_fairness_metric) && source.ml_fairness_metric.length > 0);
  const hasLlmMetric =
    (Array.isArray(source.LLM_Fairness_Metric) && source.LLM_Fairness_Metric.length > 0) ||
    (Array.isArray(source.llm_fairness_metric) && source.llm_fairness_metric.length > 0);
  if (hasMlMetric && !hasLlmMetric) return 'ml_fairness';
  if (hasLlmMetric && !hasMlMetric) return 'llm_fairness';

  const v =
    source.analysis_type ??
    source.analysisType ??
    source.AnalysisType ??
    source.kind ??
    source.run_type ??
    source.runType;
  const fromFields = analysisType(v);
  if (fromFields === 'ml_fairness' || fromFields === 'llm_fairness') return fromFields;

  const mlArr = source.ml_entries;
  const llmArr = source.llm_entries;
  const hasMl = Array.isArray(mlArr) && mlArr.length > 0;
  const hasLlm = Array.isArray(llmArr) && llmArr.length > 0;
  if (hasMl && !hasLlm) return 'ml_fairness';
  if (hasLlm && !hasMl) return 'llm_fairness';

  return fromFields;
}

function collectProjectResultRoots(projectResults: unknown): Record<string, unknown>[] {
  if (!projectResults || typeof projectResults !== 'object') return [];
  const pr = projectResults as Record<string, unknown>;
  const roots: Record<string, unknown>[] = [pr];
  const nested = pr.data ?? pr.payload ?? pr.body ?? pr.result;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    roots.push(nested as Record<string, unknown>);
  }
  const res = pr.results;
  if (res && typeof res === 'object' && !Array.isArray(res)) {
    roots.push(res as Record<string, unknown>);
  }
  const coll = pr.collection_results;
  if (coll && typeof coll === 'object' && !Array.isArray(coll)) {
    roots.push(coll as Record<string, unknown>);
  }
  const feat = pr.feature_results;
  if (feat && typeof feat === 'object' && !Array.isArray(feat)) {
    roots.push(feat as Record<string, unknown>);
  }
  return roots;
}

function collectFairnessMetricRuns(projectResults: unknown): Record<string, unknown>[] {
  const runs: Record<string, unknown>[] = [];
  const seen = new Set<string>();

  const pushRun = (item: unknown) => {
    if (!item || typeof item !== 'object' || !isWorkbenchNonRerunRun(item)) return;
    const run = item as Record<string, unknown>;
    const id = String(run.result_id ?? run._id ?? `${run.title ?? 'fairness'}-${String(run.timestamp ?? '')}`);
    if (seen.has(id)) return;
    seen.add(id);
    runs.push(run);
  };

  const pushField = (field: unknown) => {
    if (!field) return;
    if (Array.isArray(field)) field.forEach(pushRun);
    else pushRun(field);
  };

  for (const root of collectProjectResultRoots(projectResults)) {
    pushField(root.fairness_metric);
    pushField(root.fairness);
    for (const nestKey of ['collection_results', 'results', 'feature_results'] as const) {
      const nest = root[nestKey];
      if (!nest || typeof nest !== 'object') continue;
      pushField((nest as Record<string, unknown>).fairness_metric);
      pushField((nest as Record<string, unknown>).fairness);
    }
  }

  return runs;
}

/** When UI selection omits nested aggregates, pull LLM `aggregate_summary` from project fairness arrays. */
function pickLlmFairnessAggregateFromProject(projectResults: unknown): Record<string, unknown> | undefined {
  const fairnessArrays: unknown[] = [];
  for (const root of collectProjectResultRoots(projectResults)) {
    const r = root as Record<string, unknown>;
    if (r.fairness != null) fairnessArrays.push(r.fairness);
    const res = r.results as Record<string, unknown> | undefined;
    const cr = r.collection_results as Record<string, unknown> | undefined;
    const fr = r.feature_results as Record<string, unknown> | undefined;
    if (res?.fairness != null) fairnessArrays.push(res.fairness);
    if (cr?.fairness != null) fairnessArrays.push(cr.fairness);
    if (fr?.fairness != null) fairnessArrays.push(fr.fairness);
  }
  const llmRuns: Record<string, unknown>[] = [];
  for (const fa of fairnessArrays) {
    if (!Array.isArray(fa)) continue;
    for (const item of fa) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Record<string, unknown>;
      if (inferAnalysisType(row) === 'llm_fairness') llmRuns.push(row);
    }
  }
  if (llmRuns.length === 0) return undefined;
  const latest = pickLatestCollectionRun(llmRuns as object[]) as Record<string, unknown> | null;
  if (!latest || typeof latest !== 'object') return undefined;
  const agg = latest.aggregate_summary;
  if (agg && typeof agg === 'object' && !Array.isArray(agg)) return agg as Record<string, unknown>;
  return undefined;
}

interface FairnessDataItemLLM {
  Action: string;
  'Fairness Score'?: number;
  'Response Sample'?: string;
  [key: string]: string | number | undefined;
}

export interface LLMFairnessDetectionItem {
  result_id: string;
  title: string;
  timestamp: string;
  data: FairnessDataItemLLM[];
  userId?: string;
  /** API may send `user_id` instead of camelCase `userId`. */
  user_id?: string;
  aggregate_summary?: Record<string, unknown>;
}

export interface MLFairnessDetectionItem {
  result_id: string;
  title: string;
  timestamp: string;
  data: FairnessResultRow[];
  userId?: string;
  user_id?: string;
  aggregate_summary?: Record<string, unknown>;
}

/** Merge LLM + ML aggregate blobs so KPIs show scalars from either run when one is sparse. */
function mergeFairnessAggregateSummaries(
  primary: Record<string, unknown> | undefined,
  fallback: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!primary || Object.keys(primary).length === 0) return fallback ? { ...fallback } : undefined;
  if (!fallback || Object.keys(fallback).length === 0) return { ...primary };
  const out: Record<string, unknown> = { ...primary };
  const fillNested = (k: string) => {
    const pv = out[k];
    const pvEmpty =
      pv == null || (typeof pv === 'object' && !Array.isArray(pv) && Object.keys(pv as object).length === 0);
    if (pvEmpty && fallback[k] != null) out[k] = fallback[k] as (typeof out)[typeof k];
  };
  fillNested('coverage_breakdown');
  fillNested('fairness_summary');
  const scalars = [
    'risk_score',
    'fairness_risk_score',
    'overall_fairness_score',
    'dataset_verdict',
    'dataset_status',
    'overall_risk_status',
    'high_risk_count',
    'low_risk_count',
    'total_attributes',
    'resolved',
    'excluded',
    'review_required'
  ] as const;
  for (const k of scalars) {
    const pv = out[k];
    if ((pv == null || pv === '') && fallback[k] != null && fallback[k] !== '') {
      out[k] = fallback[k] as (typeof out)[typeof k];
    }
  }
  return out;
}

/** Merge nested API `Summary` (and top-level aliases) into a flat aggregate for KPI tiles. */
function normalizeFairnessAggregateForKpis(
  aggregate: Record<string, unknown> | null | undefined
): Record<string, unknown> | null | undefined {
  if (!aggregate || typeof aggregate !== 'object') return aggregate ?? undefined;
  const merged: Record<string, unknown> = { ...aggregate };
  const summary = merged.Summary as Record<string, unknown> | undefined;
  if (summary && typeof summary === 'object' && !Array.isArray(summary)) {
    if (summary.coverage_breakdown != null && typeof summary.coverage_breakdown === 'object') {
      merged.coverage_breakdown = summary.coverage_breakdown;
    }
    if (summary.overall_fairness_score !== undefined) merged.overall_fairness_score = summary.overall_fairness_score;
    if (summary.fairness_summary != null && typeof summary.fairness_summary === 'object') {
      merged.fairness_summary = summary.fairness_summary;
    }
    const oc = summary.overall_risk_status ?? summary.overall_risk_tier ?? summary.risk_tier;
    if (oc != null && merged.dataset_status == null) merged.dataset_status = String(oc);
    const rk = summary.risk_score ?? summary.Risk_Score ?? summary.overall_risk_score ?? summary.overall_risk;
    if (rk !== undefined && merged.risk_score == null) merged.risk_score = rk;
    const rt = summary.risk_tier ?? summary.Risk_Tier ?? summary.overall_risk_tier;
    if (rt != null && merged.risk_tier == null) merged.risk_tier = rt;
    const dv = summary.dataset_verdict ?? summary.verdict ?? summary.Verdict;
    if (dv != null && merged.dataset_verdict == null) merged.dataset_verdict = dv;
  }
  if (
    (merged.dataset_status == null || merged.dataset_status === '') &&
    aggregate.overall_risk_status != null &&
    String(aggregate.overall_risk_status).trim() !== ''
  ) {
    merged.dataset_status = String(aggregate.overall_risk_status);
  }
  if (merged.risk_score == null || merged.risk_score === '') {
    const alt =
      aggregate.risk_score ??
      aggregate.fairness_risk_score ??
      (aggregate as { Fairness_Risk_Score?: unknown }).Fairness_Risk_Score;
    if (alt != null && alt !== '') merged.risk_score = alt;
  }
  if (merged.risk_score == null || merged.risk_score === '') {
    const t = Number(aggregate.total_attributes);
    const h = Number(aggregate.high_risk_count);
    if (Number.isFinite(t) && t > 0 && Number.isFinite(h) && h >= 0) {
      merged.risk_score = Math.min(1, Math.max(0, h / t));
    }
  }
  const covAlt = aggregate.Coverage_Breakdown ?? aggregate.coverage_Breakdown;
  if (
    (merged.coverage_breakdown == null ||
      (typeof merged.coverage_breakdown === 'object' &&
        !Array.isArray(merged.coverage_breakdown) &&
        Object.keys(merged.coverage_breakdown as object).length === 0)) &&
    covAlt != null &&
    typeof covAlt === 'object' &&
    !Array.isArray(covAlt)
  ) {
    merged.coverage_breakdown = covAlt as Record<string, unknown>;
  }
  const fsAlt = aggregate.Fairness_Summary ?? aggregate.fairnessSummary;
  if (
    (merged.fairness_summary == null ||
      (typeof merged.fairness_summary === 'object' &&
        !Array.isArray(merged.fairness_summary) &&
        Object.keys(merged.fairness_summary as object).length === 0)) &&
    fsAlt != null &&
    typeof fsAlt === 'object' &&
    !Array.isArray(fsAlt)
  ) {
    merged.fairness_summary = fsAlt as Record<string, unknown>;
  }
  return merged;
}

/** Backend sometimes stores ML fairness as an array of version objects (result_id + data[]) instead of flat metric rows. */
function isMlFairnessVersionObject(row: Record<string, unknown>): boolean {
  if (row.result_id == null && row._id == null) return false;
  const payload = row.data ?? row.entries;
  return Array.isArray(payload);
}

function normalizeFairnessTimestamp(raw: unknown): string {
  if (raw && typeof raw === 'object' && (raw as { $date?: unknown }).$date != null) {
    const ms = (raw as { $date: number }).$date;
    return typeof ms === 'number' ? new Date(ms).toISOString() : String(ms);
  }
  return String(raw ?? '');
}

/**
 * API often exposes `fairness` as either one run object or an array of runs `[{ … }, { … }]`.
 * Attribute-level Python rows live on each run as `fairness_fairness_results`.
 */
function fairnessFairnessResultsFromFairnessField(fairnessRaw: unknown): FairnessResultRow[] | null {
  if (fairnessRaw == null) return null;

  const rowsFromRun = (run: Record<string, unknown>): FairnessResultRow[] | null => {
    const nested = run.fairness_fairness_results ?? run.fairnessFairnessResults;
    if (Array.isArray(nested) && nested.length > 0) return nested as FairnessResultRow[];
    return null;
  };

  if (Array.isArray(fairnessRaw)) {
    const latest = pickLatestCollectionRun(fairnessRaw as object[]) as Record<string, unknown> | null;
    if (latest && typeof latest === 'object') {
      const fromLatest = rowsFromRun(latest);
      if (fromLatest) return fromLatest;
    }
    for (const item of fairnessRaw) {
      if (!item || typeof item !== 'object') continue;
      const fromItem = rowsFromRun(item as Record<string, unknown>);
      if (fromItem) return fromItem;
    }
    return null;
  }

  if (typeof fairnessRaw === 'object') {
    const fromOne = rowsFromRun(fairnessRaw as Record<string, unknown>);
    if (fromOne) return fromOne;
  }

  return null;
}

function resolveFairnessFairnessResultsRows(projectResults: unknown): FairnessResultRow[] | null {
  for (const pr of collectProjectResultRoots(projectResults)) {
    const cr = pr.collection_results;
    const crObj = cr && typeof cr === 'object' ? (cr as Record<string, unknown>) : null;
    const res = pr.results;
    const resObj = res && typeof res === 'object' ? (res as Record<string, unknown>) : null;
    const fr = pr.feature_results;
    const frObj = fr && typeof fr === 'object' ? (fr as Record<string, unknown>) : null;

    const candidates: unknown[] = [
      crObj?.fairness_fairness_results,
      resObj?.fairness_fairness_results,
      frObj?.fairness_fairness_results,
      pr.fairness_fairness_results,
      crObj?.fairness_results,
      resObj?.fairness_results,
      frObj?.fairness_results
    ];

    for (const c of candidates) {
      if (Array.isArray(c) && c.length > 0) return c as FairnessResultRow[];
    }

    const fairnessBuckets = [crObj?.fairness, resObj?.fairness, frObj?.fairness, pr.fairness];
    for (const fb of fairnessBuckets) {
      const nested = fairnessFairnessResultsFromFairnessField(fb);
      if (nested && nested.length > 0) return nested;
    }
  }

  return null;
}

export interface FairnessDetectionRef {
  handleHistoryOpen: (type: 'ml' | 'llm') => void;
}

export function FairnessVersionHistoryStrip({
  llmCount,
  mlCount,
  onOpenLlm,
  onOpenMl
}: {
  llmCount: number;
  mlCount: number;
  onOpenLlm: () => void;
  onOpenMl: () => void;
}) {
  if (llmCount === 0 && mlCount === 0) {
    return null;
  }

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 gap-sm-3 lh-sm small text-body-secondary">
      {llmCount > 0 && (
        <span className="d-inline-flex align-items-center gap-2">
          <span>
            LLM <span className="text-body fw-medium">{llmCount}</span> {llmCount === 1 ? 'version' : 'versions'}
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm text-decoration-none p-0 border-0"
            style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={onOpenLlm}
          >
            History
          </button>
        </span>
      )}
      {llmCount > 0 && mlCount > 0 && (
        <span className="text-body-tertiary d-none d-sm-inline" aria-hidden="true">
          {'·'}
        </span>
      )}
      {mlCount > 0 && (
        <span className="d-inline-flex align-items-center gap-2">
          <span>
            ML <span className="text-body fw-medium">{mlCount}</span> {mlCount === 1 ? 'version' : 'versions'}
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm text-decoration-none p-0 border-0"
            style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={onOpenMl}
          >
            History
          </button>
        </span>
      )}
    </div>
  );
}

interface FairnessMatrixProps {
  showHeaderVersionStrip?: boolean;
  onVersionsReady?: (mlCount: number, llmCount: number) => void;
}

const FairnessMatrix = forwardRef<FairnessDetectionRef, FairnessMatrixProps>(
  ({ showHeaderVersionStrip = true, onVersionsReady }, ref) => {
    const projectResults = useSelector(selectProjectResults);

    const [mlFairnessVersions, setMlFairnessVersions] = useState<MLFairnessDetectionItem[]>([]);
    const [llmFairnessVersions, setLlmFairnessVersions] = useState<LLMFairnessDetectionItem[]>([]);
    const [selectedMlVersion, setSelectedMlVersion] = useState<MLFairnessDetectionItem | null>(null);
    const [selectedLlmVersion, setSelectedLlmVersion] = useState<LLMFairnessDetectionItem | null>(null);
    const [isHeaderSticky, setIsHeaderSticky] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const stickyTopPxRef = useRef(readPhoenixNavbarTopPx());
    const [historyOpen, setHistoryOpen] = useState(false);
    const [activeHistoryType, setActiveHistoryType] = useState<'ml' | 'llm'>('llm');
    const [fairnessAccordionKey, setFairnessAccordionKey] = useState<string | null>('llm');
    const onVersionsReadyRef = useRef(onVersionsReady);
    onVersionsReadyRef.current = onVersionsReady;

    const llmFairnessData = useMemo(() => selectedLlmVersion?.data || [], [selectedLlmVersion]);

    const fairnessKpiAggregate = useMemo(() => {
      const fromSelections = mergeFairnessAggregateSummaries(
        selectedLlmVersion?.aggregate_summary as Record<string, unknown> | undefined,
        selectedMlVersion?.aggregate_summary as Record<string, unknown> | undefined
      );
      const hasSelectionAgg = fromSelections != null && Object.keys(fromSelections).length > 0;
      const merged = hasSelectionAgg
        ? fromSelections
        : mergeFairnessAggregateSummaries(fromSelections, pickLlmFairnessAggregateFromProject(projectResults));
      return normalizeFairnessAggregateForKpis(merged ?? fromSelections);
    }, [selectedLlmVersion, selectedMlVersion, projectResults]);

    const llmHeaders = useMemo(
      () => (llmFairnessData.length > 0 ? Object.keys(llmFairnessData[0]) : ['Action']),
      [llmFairnessData]
    );

    const fairnessSources = useMemo(() => {
      const sources: any[] = [];
      const pushIfObject = (candidate?: any) => {
        if (candidate && typeof candidate === 'object') {
          if (Array.isArray(candidate)) {
            candidate.forEach(item => {
              if (item && typeof item === 'object') sources.push(item);
            });
          } else {
            sources.push(candidate);
          }
        }
      };

      const ingestBucket = (bucket?: Record<string, unknown>) => {
        if (!bucket || typeof bucket !== 'object') return;
        pushIfObject(bucket.fairness_metric);
        pushIfObject(bucket.fairness);
        const cr = bucket.collection_results;
        const res = bucket.results;
        const fr = bucket.feature_results;
        if (cr && typeof cr === 'object') pushInner(cr as Record<string, unknown>);
        if (res && typeof res === 'object') pushInner(res as Record<string, unknown>);
        if (fr && typeof fr === 'object') pushInner(fr as Record<string, unknown>);
      };

      const pushInner = (inner?: Record<string, unknown>) => {
        if (!inner || typeof inner !== 'object') return;
        pushIfObject(inner.fairness_metric);
        pushIfObject(inner.fairness);
      };

      const pushBucketMlFairness = (bucket?: Record<string, unknown>) => {
        if (!bucket || typeof bucket !== 'object') return;
        pushIfObject(bucket.ml_fairness_metric);
        pushIfObject(bucket.ML_Fairness_Metric_Versions);
        pushIfObject(bucket.ml_fairness_versions);
        pushIfObject(bucket.ml_fairness);
        pushIfObject(bucket.ML_Fairness_Results);
        pushIfObject(bucket.ml_fairness_results);

        const attachFlatMlMetric = (candidate: unknown) => {
          if (!Array.isArray(candidate) || candidate.length === 0) return;
          const first = candidate[0];
          if (!first || typeof first !== 'object') return;
          if (isMlFairnessVersionObject(first as Record<string, unknown>)) {
            pushIfObject(candidate);
            return;
          }
          sources.push({
            result_id: bucket.result_id ?? bucket._id,
            title: bucket.title,
            timestamp: bucket.timestamp ?? bucket.update_date ?? bucket.upload_date,
            userId: bucket.userId ?? bucket.user_id,
            ML_Fairness_Metric: candidate,
            analysis_type: bucket.analysis_type ?? 'ml_fairness',
            aggregate_summary: bucket.aggregate_summary
          });
        };

        attachFlatMlMetric(bucket.ML_Fairness_Metric);
        attachFlatMlMetric(bucket.ml_fairness_metric);
      };

      const pushBucketLlmFairness = (bucket?: Record<string, unknown>) => {
        if (!bucket || typeof bucket !== 'object') return;
        pushIfObject(bucket.llm_fairness_metric);
        pushIfObject(bucket.LLM_Fairness_Metric);
        pushIfObject(bucket.LLM_Fairness_Metric_Versions);
        pushIfObject(bucket.llm_fairness_versions);
        pushIfObject(bucket.llm_fairness);
      };

      for (const root of collectProjectResultRoots(projectResults)) {
        ingestBucket(root);
        const cr = root.collection_results;
        const res = root.results;
        const fr = root.feature_results;
        if (cr && typeof cr === 'object') {
          pushBucketMlFairness(cr as Record<string, unknown>);
          pushBucketLlmFairness(cr as Record<string, unknown>);
        }
        if (res && typeof res === 'object') {
          pushBucketMlFairness(res as Record<string, unknown>);
          pushBucketLlmFairness(res as Record<string, unknown>);
        }
        if (fr && typeof fr === 'object') {
          pushBucketMlFairness(fr as Record<string, unknown>);
          pushBucketLlmFairness(fr as Record<string, unknown>);
        }
      }

      return sources;
    }, [projectResults]);

    useEffect(() => {
      const mlCollected: MLFairnessDetectionItem[] = [];
      const llmCollected: LLMFairnessDetectionItem[] = [];
      const isRecord = (value: unknown): value is Record<string, unknown> =>
        Boolean(value) && typeof value === 'object';

      const readUserId = (src: Record<string, unknown> | undefined): string => {
        const v = src?.userId ?? src?.user_id;
        return typeof v === 'string' && v.length > 0 ? v : 'System';
      };

      const readUserIdPrefer = (
        primary: Record<string, unknown> | undefined,
        fallback: Record<string, unknown> | undefined
      ): string => {
        const id = primary?.userId ?? primary?.user_id;
        if (typeof id === 'string' && id.length > 0) return id;
        return readUserId(fallback);
      };

      const collectLlmFromSource = (source: any) => {
        if (!isWorkbenchNonRerunRun(source)) return;
        const src = source as Record<string, unknown>;
        const at = inferAnalysisType(src);
        const hasLlmPayloadFields =
          (Array.isArray(source?.LLM_Fairness_Metric) && source.LLM_Fairness_Metric.length > 0) ||
          (Array.isArray(source?.llm_fairness_metric) && source.llm_fairness_metric.length > 0) ||
          (Array.isArray(source?.llm_entries) && source.llm_entries.length > 0);
        if (at === 'ml_fairness' && !hasLlmPayloadFields) return;

        const llmCandidates = [
          source?.LLM_Fairness_Metric,
          source?.llmFairnessMetric,
          source?.llm_fairness_metric,
          source?.llm_entries,
          source?.fairness_data_llm,
          source?.llm_data,
          source?.llm,
          source?.LLM
        ];
        const hasLlmPayload = llmCandidates.some(candidate => {
          if (Array.isArray(candidate)) return candidate.length > 0;
          return candidate != null && typeof candidate === 'object';
        });

        const entries: any[] = [];
        llmCandidates.filter(Boolean).forEach(candidate => {
          if (Array.isArray(candidate)) {
            candidate.filter(isRecord).forEach(entry => entries.push(entry));
          } else if (isRecord(candidate)) {
            const arr = candidate.entries || candidate.data;
            if (Array.isArray(arr)) {
              arr.filter(isRecord).forEach(entry => entries.push(entry));
            }
          }
        });

        const title = source?.title || 'LLM Fairness';
        let timestamp = source?.timestamp || source?.update_date || source?.upload_date || '';

        if (timestamp && typeof timestamp === 'object' && (timestamp as { $date?: unknown }).$date != null) {
          const ms = (timestamp as { $date: number }).$date;
          timestamp = typeof ms === 'number' ? new Date(ms).toISOString() : String(ms);
        }

        const result_id = source?.result_id || source?._id || `${title}-${JSON.stringify(timestamp)}`;

        const aggRaw = (source as Record<string, unknown> | undefined)?.aggregate_summary;
        const aggregate_summary: Record<string, unknown> =
          aggRaw != null && typeof aggRaw === 'object' && !Array.isArray(aggRaw)
            ? (aggRaw as Record<string, unknown>)
            : {};

        if (hasLlmPayload || entries.length > 0) {
          llmCollected.push({
            result_id,
            title,
            timestamp: String(timestamp ?? ''),
            data: entries,
            userId: readUserId(source as Record<string, unknown>),
            aggregate_summary
          });
        }
      };

      collectFairnessMetricRuns(projectResults).forEach(run => {
        const mlArr = (run.ML_Fairness_Metric ?? run.ml_fairness_metric) as unknown;
        if (!Array.isArray(mlArr) || mlArr.length === 0) return;
        const first = mlArr[0];
        if (isRecord(first) && isMlFairnessVersionObject(first)) return;

        const title = String(run.title ?? 'ML Fairness');
        const timestamp = normalizeFairnessTimestamp(run.timestamp ?? run.update_date ?? run.upload_date ?? '');
        const result_id = String(run.result_id ?? run._id ?? `ml-${title}-${timestamp}`);
        const data = mlArr.filter(isRecord).map(entry => entryToMlFairnessRow(entry));
        if (data.length === 0) return;

        const aggRaw = run.aggregate_summary;
        const aggregate_summary: Record<string, unknown> =
          aggRaw != null && typeof aggRaw === 'object' && !Array.isArray(aggRaw)
            ? (aggRaw as Record<string, unknown>)
            : {};

        mlCollected.push({
          result_id,
          title,
          timestamp,
          data,
          userId: readUserId(run),
          aggregate_summary
        });
      });

      fairnessSources.forEach(collectLlmFromSource);
      const collectMlFromSource = (source: any) => {
        if (!isWorkbenchNonRerunRun(source)) return;
        const at = inferAnalysisType(source as Record<string, unknown>);
        const hasMlPayload =
          (Array.isArray(source?.ML_Fairness_Metric) && source.ML_Fairness_Metric.length > 0) ||
          (Array.isArray(source?.ml_fairness_metric) && source.ml_fairness_metric.length > 0) ||
          (Array.isArray(source?.ml_entries) && source.ml_entries.length > 0) ||
          (Array.isArray(source?.fairness_fairness_results) && source.fairness_fairness_results.length > 0);
        if (at === 'llm_fairness' && !hasMlPayload) return;

        const readMlAggregate = (node: Record<string, unknown> | undefined): Record<string, unknown> => {
          const aggRaw = node?.aggregate_summary;
          if (aggRaw != null && typeof aggRaw === 'object' && !Array.isArray(aggRaw)) {
            return aggRaw as Record<string, unknown>;
          }
          return {};
        };

        const entries: FairnessResultRow[] = [];
        const pushFlatArray = (candidate: unknown) => {
          if (!Array.isArray(candidate)) return;
          candidate.filter(isRecord).forEach(entry => entries.push(entryToMlFairnessRow(entry)));
        };

        pushFlatArray(source?.ml_entries);
        pushFlatArray(source?.fairness_fairness_results);

        let expandedFromVersionArrays = 0;
        const versionExpandKeys: unknown[] = [source?.ML_Fairness_Metric, source?.ml_fairness_metric];
        versionExpandKeys.filter(Boolean).forEach(candidate => {
          if (!Array.isArray(candidate) || candidate.length === 0) return;
          const first = candidate[0];
          if (!isRecord(first) || !isMlFairnessVersionObject(first)) return;
          candidate.filter(isRecord).forEach(vObj => {
            if (!isWorkbenchNonRerunRun(vObj)) return;
            const rowsRaw = vObj.data ?? vObj.entries ?? vObj.ml_entries ?? [];
            const data = Array.isArray(rowsRaw)
              ? rowsRaw.filter(isRecord).map(entry => entryToMlFairnessRow(entry))
              : [];
            const title = String(vObj.title ?? source?.title ?? 'ML Fairness');
            const timestamp = normalizeFairnessTimestamp(
              vObj.timestamp ?? vObj.update_date ?? vObj.upload_date ?? source?.timestamp ?? ''
            );
            const result_id = String(vObj.result_id ?? vObj._id ?? `ml-${title}-${timestamp}`);
            mlCollected.push({
              result_id,
              title,
              timestamp,
              data,
              userId: readUserIdPrefer(vObj as Record<string, unknown>, source as Record<string, unknown>),
              aggregate_summary: readMlAggregate(vObj as Record<string, unknown>)
            });
            expandedFromVersionArrays += 1;
          });
        });

        if (expandedFromVersionArrays > 0) {
          if (entries.length > 0) {
            const titleFlush = source?.title || 'ML Fairness';
            const timestampFlush = normalizeFairnessTimestamp(
              source?.timestamp ?? source?.update_date ?? source?.upload_date ?? ''
            );
            const resultIdFlush =
              source?.result_id || source?._id || `ml-${titleFlush}-${JSON.stringify(timestampFlush)}`;
            mlCollected.push({
              result_id: resultIdFlush,
              title: titleFlush,
              timestamp: timestampFlush,
              data: entries,
              userId: readUserId(source as Record<string, unknown>),
              aggregate_summary: readMlAggregate(source as Record<string, unknown>)
            });
          }
          return;
        }

        pushFlatArray(source?.ML_Fairness_Metric);
        pushFlatArray(source?.ml_fairness_metric);

        const restCandidates = [
          source?.ML_Fairness_Results,
          source?.ML_Fairness_Detection,
          source?.ml_fairness_results,
          source?.mlFairnessMetric,
          source?.ml
        ];

        restCandidates.filter(Boolean).forEach(candidate => {
          if (Array.isArray(candidate)) {
            candidate.filter(isRecord).forEach(entry => entries.push(entryToMlFairnessRow(entry)));
          } else if (isRecord(candidate)) {
            const arr = candidate.entries || candidate.data;
            if (Array.isArray(arr)) {
              arr.filter(isRecord).forEach(entry => entries.push(entryToMlFairnessRow(entry)));
            }
          }
        });

        const sourceAgg = readMlAggregate(source as Record<string, unknown>);
        const hasAgg = Object.keys(sourceAgg).length > 0;
        if (entries.length === 0 && !hasAgg) return;
        const title = source?.title || 'ML Fairness';
        const timestamp = normalizeFairnessTimestamp(
          source?.timestamp ?? source?.update_date ?? source?.upload_date ?? ''
        );
        const result_id = source?.result_id || source?._id || `ml-${title}-${JSON.stringify(timestamp)}`;
        mlCollected.push({
          result_id,
          title,
          timestamp,
          data: entries,
          userId: readUserId(source as Record<string, unknown>),
          aggregate_summary: sourceAgg
        });
      };
      fairnessSources.forEach(collectMlFromSource);

      const fallbackMl = resolveFairnessFairnessResultsRows(projectResults);
      if (fallbackMl && fallbackMl.length > 0) {
        const pr = projectResults as Record<string, unknown> | null | undefined;
        const cr = pr?.collection_results as Record<string, unknown> | undefined;
        const res = pr?.results as Record<string, unknown> | undefined;
        const fr = pr?.feature_results as Record<string, unknown> | undefined;
        const fallbackTimestamp = normalizeFairnessTimestamp(cr?.timestamp ?? res?.timestamp ?? fr?.timestamp ?? '');
        mlCollected.push({
          result_id: `ml-fallback-${fallbackTimestamp || 'default'}`,
          title: 'ML Fairness Detection',
          timestamp: fallbackTimestamp,
          data: fallbackMl,
          userId: 'System'
        });
      }

      const dedupe = <T extends { result_id: string }>(items: T[]): T[] => {
        const seen = new Set<string>();
        return items.filter(item => {
          if (seen.has(item.result_id)) return false;
          seen.add(item.result_id);
          return true;
        });
      };

      const mlDeduped = dedupe(mlCollected);
      const llmDeduped = dedupe(llmCollected);

      setMlFairnessVersions(mlDeduped);
      setLlmFairnessVersions(llmDeduped);

      setSelectedMlVersion(prev => {
        if (mlDeduped.length === 0) return null;
        if (prev?.result_id) {
          const match = mlDeduped.find(v => v.result_id === prev.result_id);
          if (match) return match;
        }
        return mlDeduped[0];
      });

      setSelectedLlmVersion(prev => {
        if (llmDeduped.length === 0) return null;
        if (prev?.result_id) {
          const match = llmDeduped.find(v => v.result_id === prev.result_id);
          if (match) return match;
        }
        return llmDeduped[0];
      });

      onVersionsReadyRef.current?.(mlDeduped.length, llmDeduped.length);
    }, [fairnessSources, projectResults]);

    useLayoutEffect(() => {
      const syncTop = () => {
        stickyTopPxRef.current = readPhoenixNavbarTopPx();
      };
      syncTop();
      window.addEventListener('resize', syncTop);
      return () => window.removeEventListener('resize', syncTop);
    }, []);

    useEffect(() => {
      const handleScroll = () => {
        if (headerRef.current) {
          const rect = headerRef.current.getBoundingClientRect();
          setIsHeaderSticky(rect.top <= stickyTopPxRef.current + 0.5);
        }
      };

      handleScroll();
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleVersionSelect = (type: 'ml' | 'llm', version: MLFairnessDetectionItem | LLMFairnessDetectionItem) => {
      if (type === 'ml') {
        setSelectedMlVersion(version as MLFairnessDetectionItem);
        return;
      }
      setSelectedLlmVersion(version as LLMFairnessDetectionItem);
    };

    const modifierFromVersion = (version: MLFairnessDetectionItem | LLMFairnessDetectionItem): string => {
      const id = version.userId ?? version.user_id;
      return typeof id === 'string' && id.length > 0 ? id : 'System';
    };

    const convertToVersionItems = (versions: (MLFairnessDetectionItem | LLMFairnessDetectionItem)[]): VersionItem[] =>
      versions.map((version, index) => {
        const versionId = version.result_id || `fairness-${activeHistoryType}-${index}`;
        return {
          id: versionId,
          timestamp: version.timestamp,
          date: version.timestamp,
          modifier: modifierFromVersion(version),
          isCurrent:
            activeHistoryType === 'llm'
              ? selectedLlmVersion?.result_id === version.result_id
              : selectedMlVersion?.result_id === version.result_id,
          title: version.title || `Version ${index + 1}`
        };
      });

    const handleHistoryOpen = (type: 'ml' | 'llm') => {
      setActiveHistoryType(type);
      setFairnessAccordionKey(type);
      setHistoryOpen(true);
    };

    useImperativeHandle(ref, () => ({
      handleHistoryOpen
    }));

    const handleVersionHistorySelect = (versionItem: VersionItem) => {
      const versions = activeHistoryType === 'llm' ? llmFairnessVersions : mlFairnessVersions;
      const version = versions.find(
        v => v.result_id === versionItem.id || String(v.result_id) === String(versionItem.id)
      );
      if (version) {
        handleVersionSelect(activeHistoryType, version);
        setHistoryOpen(false);
      }
    };

    const llmFairnessPlotData = useMemo((): FairnessResultRow[] => {
      if (!selectedLlmVersion?.data?.length) return [];
      return selectedLlmVersion.data
        .filter((row): row is FairnessDataItemLLM => row != null && typeof row === 'object')
        .map(row => toFairnessResultRow(row as unknown as Record<string, unknown>));
    }, [selectedLlmVersion]);

    const mlFairnessPlotData = useMemo((): FairnessResultRow[] => {
      if (selectedMlVersion?.data?.length) return selectedMlVersion.data;
      if (mlFairnessVersions.length === 0) return [];
      const raw = resolveFairnessFairnessResultsRows(projectResults);
      return raw && raw.length > 0 ? raw : [];
    }, [selectedMlVersion, mlFairnessVersions.length, projectResults]);

    const getBadgeBg = (risk: string | number | unknown) => {
      const riskStr = risk != null ? String(risk) : '';
      switch (riskStr.toLowerCase()) {
        case 'low':
          return 'success';
        case 'moderate':
          return 'warning';
        case 'high':
          return 'danger';
        default:
          return 'secondary';
      }
    };

    const renderTableHeaders = (headers: string[]) => headers.map((header, index) => <th key={index}>{header}</th>);
    const renderTableRowsLLM = (data: FairnessDataItemLLM[], headers: string[]) =>
      data.map((row, rowIndex) => (
        <tr key={rowIndex}>
          {headers.map((header, colIndex) => (
            <td key={colIndex}>
              {header === 'Risk' ? (
                <Badge bg={getBadgeBg(row[header] as string)} variant="phoenix" iconPosition="end" className="fs-10">
                  {row[header]}
                </Badge>
              ) : typeof row[header] === 'number' ? (
                (row[header] as number).toFixed(2)
              ) : (
                String(row[header] ?? '-')
              )}
            </td>
          ))}
        </tr>
      ));

    return (
      <div>
        {showHeaderVersionStrip ? (
          <div
            ref={headerRef}
            style={{
              position: 'sticky',
              top: 'var(--phoenix-navbar-top-height, 72px)',
              zIndex: 1100,
              backgroundColor: isHeaderSticky ? 'var(--bs-body-bg)' : 'transparent',
              borderBottom: isHeaderSticky ? '1px solid var(--bs-border-color-translucent)' : 'none',
              backdropFilter: isHeaderSticky ? 'blur(8px)' : 'none',
              boxShadow: isHeaderSticky ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
              marginBottom: '4px',
              transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
              width: '100%',
              left: 0,
              right: 0
            }}
          >
            <Container fluid className="px-0 px-sm-1 px-lg-2">
              <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 py-0 min-h-0">
                <FairnessVersionHistoryStrip
                  llmCount={llmFairnessVersions.length}
                  mlCount={mlFairnessVersions.length}
                  onOpenLlm={() => handleHistoryOpen('llm')}
                  onOpenMl={() => handleHistoryOpen('ml')}
                />
              </div>
            </Container>
          </div>
        ) : (
          <div ref={headerRef} className="d-none" aria-hidden />
        )}

        <Container fluid className="px-0 px-sm-1 px-lg-2">
          <Row className="g-1 mb-0">
            <Col xs={12}>
              <Card className="border-0 shadow-sm rounded-4 overflow-hidden bg-body-secondary bg-opacity-10">
                <Card.Body className="p-2 p-md-2">
                  <FairnessModuleKpiGrid aggregate={fairnessKpiAggregate} />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Accordion
            activeKey={fairnessAccordionKey}
            onSelect={k => setFairnessAccordionKey(typeof k === 'string' ? k : null)}
            className="bias-detection-accordion border-0 rounded-4 shadow-sm mb-2 overflow-hidden"
          >
            <Accordion.Item eventKey="llm" className="border-0 bg-white">
              <Accordion.Header className="bias-detection-accordion__header px-2 py-1 fw-semibold">
                LLM Fairness Analysis
              </Accordion.Header>
              <Accordion.Body className="p-2">
                {llmFairnessVersions.length > 0 && (!selectedLlmVersion || !selectedLlmVersion.data?.length) ? (
                  <Typography variant="body2" color="text.secondary" className="py-1">
                    No LLM fairness rows for this run. Attribute-level charts below may still use project-level or
                    sample data where available.
                  </Typography>
                ) : selectedLlmVersion && llmFairnessVersions.length > 0 ? (
                  <Typography variant="subtitle2" color="text.secondary" className="mb-2">
                    {selectedLlmVersion.title}
                  </Typography>
                ) : null}

                <Typography variant="subtitle1" fontWeight={600} color="text.primary" className="mb-1">
                  Attribute-Level Fairness
                </Typography>
                <FairnessPlotsSection data={llmFairnessPlotData} height={220} showSummary={false} />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="ml" className="border-0 bg-white">
              <Accordion.Header className="bias-detection-accordion__header px-2 py-1 fw-semibold">
                ML Fairness Analysis
              </Accordion.Header>
              <Accordion.Body className="p-2">
                {mlFairnessVersions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" className="py-1">
                    No ML fairness versions yet. Run fairness matrix on dataset or ML model uploads, or pick a version
                    from ML History in the tab bar.
                  </Typography>
                ) : (
                  <>
                    {selectedMlVersion ? (
                      <Typography variant="subtitle2" color="text.secondary" className="mb-2">
                        {selectedMlVersion.title}
                      </Typography>
                    ) : null}
                    {mlFairnessPlotData.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" className="py-1">
                        No ML fairness attribute rows for the selected version. Choose another run from ML History.
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="subtitle1" fontWeight={600} color="text.primary" className="mb-1">
                          Attribute-Level Fairness
                        </Typography>
                        <FairnessPlotsSection data={mlFairnessPlotData} height={220} showSummary={false} />
                        <div className="table-responsive mt-2">
                          <Table striped bordered hover className="table-sm align-middle mb-0">
                            <thead>
                              <tr>
                                <th>Attribute</th>
                                <th>Status</th>
                                <th>Audit status</th>
                                <th>Disparity</th>
                                <th>Confidence</th>
                              </tr>
                            </thead>
                            <tbody>
                              {mlFairnessPlotData.map(row => (
                                <tr key={row.attribute}>
                                  <td className="fw-semibold">{row.attribute || '—'}</td>
                                  <td>{row.status || '—'}</td>
                                  <td>{row.audit_status || '—'}</td>
                                  <td>{row.disparity != null ? row.disparity.toFixed(3) : '—'}</td>
                                  <td>{Number.isFinite(row.confidence) ? row.confidence.toFixed(3) : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    )}
                  </>
                )}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {llmFairnessVersions.length > 0 && (
            <Card className="border-0 shadow-sm rounded-4 mb-2 overflow-hidden bg-white">
              <Card.Body className="p-2 p-md-3">
                <Typography variant="subtitle1" component="h3" className="fw-semibold mb-2">
                  LLM fairness - Request Data Analysis
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" className="mb-2">
                  Fairness Metric - Disparate Impact LLM Model
                </Typography>
                <div className="table-responsive">
                  <Table striped bordered hover className="table-sm align-middle mb-0">
                    <thead>
                      <tr>{renderTableHeaders(llmHeaders)}</tr>
                    </thead>
                    <tbody>{renderTableRowsLLM(llmFairnessData, llmHeaders)}</tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}
        </Container>
        <VersionHistory
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          versions={convertToVersionItems(activeHistoryType === 'llm' ? llmFairnessVersions : mlFairnessVersions)}
          onVersionSelect={handleVersionHistorySelect}
          title={`Version History - ${activeHistoryType.toUpperCase()}`}
        />
      </div>
    );
  }
);

FairnessMatrix.displayName = 'FairnessMatrix';

export default FairnessMatrix;
